import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutGrid, LayoutList, Plus, Trash2 } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  tableRowClassName,
} from '../../components/ui/table';
import { TableActions, TableActionButton, tableActionsColumnClass } from '../../components/ui/table-actions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { TooltipIconButton } from '../../components/ui/tooltip-icon-button';
import { CardListSkeleton, PageHeaderSkeleton, PAGE_SHELL, Skeleton } from '../../components/ui/skeleton';
import { deleteEvent, fetchAllEvents } from '../../lib/events';
import { toast } from '../../lib/toast';

function EventsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => {
    fetchAllEvents()
      .then(setEvents)
      .catch(() => {
        toast.error('Errore di caricamento', 'Impossibile recuperare l\'elenco eventi.');
      })
      .finally(() => setLoading(false));
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEvent(deleteTarget.id);
      setEvents((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      toast.success('Evento eliminato', `"${deleteTarget.name}" è stato rimosso.`);
    } catch (e) {
      console.error(e);
      toast.error('Eliminazione non riuscita', 'Riprova tra qualche istante.');
    }
    setDeleteTarget(null);
  };

  return (
    <div className={PAGE_SHELL}>
      {loading ? (
        <>
          <PageHeaderSkeleton />
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-9 w-32 rounded-lg" />
          </div>
          <CardListSkeleton count={4} />
        </>
      ) : (
        <>
      <PageHeader
        title="Eventi"
        description="Organizza i turni leggendo le risposte di un invito: assegna i partecipanti ai negozi e esporta il piano."
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="inline-flex h-auto w-max flex-nowrap justify-start gap-0.5 p-1">
            <TabsTrigger value="table" className="shrink-0 px-3">
              <LayoutList className="h-4 w-4" />
              Tabella
            </TabsTrigger>
            <TabsTrigger value="cards" className="shrink-0 px-3">
              <LayoutGrid className="h-4 w-4" />
              Card
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Button asChild>
          <Link to="/eventi/nuovo">
            <Plus className="h-4 w-4" />
            Nuovo evento
          </Link>
        </Button>
      </div>

      {events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center ba-muted">
            Nessun evento ancora.{' '}
            <Link to="/eventi/nuovo" className="font-semibold text-brand-orange hover:underline">
              Creane uno
            </Link>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">Nome</TableHead>
                  <TableHead className="min-w-[160px]">Invito sorgente</TableHead>
                  <TableHead className="min-w-[90px]">Negozi</TableHead>
                  <TableHead className="min-w-[120px]">Assegnazioni</TableHead>
                  <TableHead className="min-w-[140px]">Aggiornato</TableHead>
                  <TableHead className={tableActionsColumnClass({ minWidth: 'min-w-[12rem]' })}>
                    Azioni
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((ev, index) => (
                  <motion.tr
                    key={ev.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2, delay: index * 0.02 }}
                    className={tableRowClassName}
                  >
                    <TableCell className="font-medium">{ev.name || '—'}</TableCell>
                    <TableCell className="text-sm text-[var(--ba-muted)]">{ev.sourceInvitationId || '—'}</TableCell>
                    <TableCell>{ev.stores?.length || 0}</TableCell>
                    <TableCell>{ev.assignments?.length || 0}</TableCell>
                    <TableCell className="text-sm text-[var(--ba-muted)]">
                      {ev.updatedAt ? new Date(ev.updatedAt).toLocaleDateString('it-IT') : '—'}
                    </TableCell>
                    <TableCell className={tableActionsColumnClass({ minWidth: 'min-w-[12rem]' })}>
                      <TableActions>
                        <TableActionButton
                          label="Apri planner"
                          icon={LayoutGrid}
                          onClick={() => { window.location.href = `/eventi/${ev.id}`; }}
                        />
                        <TableActionButton
                          label="Elimina evento"
                          icon={Trash2}
                          variant="destructive"
                          onClick={() => setDeleteTarget(ev)}
                        />
                      </TableActions>
                    </TableCell>
                  </motion.tr>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev, i) => (
            <motion.div
              key={ev.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
            >
              <Card className="transition-shadow hover:shadow-md">
                <CardContent className="flex h-full flex-col gap-3 p-4">
                  <div className="flex flex-1 flex-col gap-3">
                    <div className="flex flex-nowrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="line-clamp-2 font-bold leading-snug">{ev.name || '—'}</h3>
                        <p className="mt-1 line-clamp-1 text-sm text-[var(--ba-muted)]">
                          Invito: {ev.sourceInvitationId || '—'}
                        </p>
                      </div>
                      <Badge variant="secondary" className="shrink-0 whitespace-nowrap self-start">
                        {ev.stores?.length || 0} negozi
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2 dark:bg-white/[0.02]">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ba-muted)]">Assegnazioni</p>
                        <p className="text-xl font-bold tabular-nums text-brand-orange">{ev.assignments?.length || 0}</p>
                      </div>
                      <div className="rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2 dark:bg-white/[0.02]">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ba-muted)]">Aggiornato</p>
                        <p className="text-sm font-semibold tabular-nums">
                          {ev.updatedAt ? new Date(ev.updatedAt).toLocaleDateString('it-IT') : '—'}
                        </p>
                      </div>
                    </div>

                    <div className="min-h-[2.25rem]">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--ba-muted)]">
                        {ev.sourceInvitationId ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ba-surface-border)] bg-black/[0.02] px-2 py-1 font-medium text-[var(--ba-muted)] dark:bg-white/[0.02]">
                            Pronto per pianificazione
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ba-surface-border)] bg-black/[0.02] px-2 py-1 font-medium text-[var(--ba-muted)] dark:bg-white/[0.02]">
                            Nessun insight disponibile
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-1 border-t border-[var(--ba-surface-border)] pt-3">
                    <div className="flex items-center justify-between gap-2">
                      <Button asChild variant="outline" className="w-full justify-center">
                        <Link to={`/eventi/${ev.id}`}>Apri planner</Link>
                      </Button>
                      <TooltipIconButton
                        label="Elimina evento"
                        icon={Trash2}
                        variant="destructive"
                        onClick={() => setDeleteTarget(ev)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

        </>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminare l&apos;evento?</DialogTitle>
            <DialogDescription>
              Stai per eliminare &quot;{deleteTarget?.name}&quot;. Il piano turni verrà perso (le risposte dell&apos;invito restano intatte).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button variant="destructive" onClick={confirmDelete}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EventsList;
